import { useMemo, useState, useCallback, useEffect } from 'react';
import BookingDetailDrawer from './BookingDetailDrawer';
import { isImportedInHouseBooking, isCurrentlyInHouse } from '@/pages/dashboard/utils/bookingOperations';

// ── Types ──

export interface OpRoom {
  id: string;
  name: string;
}

export interface OpBooking {
  id: string;
  room_id: string;
  guest_display_name: string | null;
  arrival_date: string;
  arrival_time: string | null;
  departure_date: string;
  departure_time: string | null;
  booking_status: string | null;
  operational_status: string | null;
  booking_channel: string | null;
  source_override: string | null;
  adults: number;
  children: number;
  infants: number;
  beds24_booking_id: string | null;
  beds24_status: string | null;
  actual_check_in_at: string | null;
  actual_check_out_at: string | null;
  checked_in_by: string | null;
  checked_out_by: string | null;
  booking_origin: string | null;
}

export interface OpTask {
  id: string;
  room_id: string;
  status: string;
  task_date: string;
  departing_booking_id: string | null;
}

export interface OpGuestOp {
  id: string;
  booking_id: string;
  early_checkin_requested: string | null;
  early_checkin_approved: string | null;
  late_checkout_requested: string | null;
  late_checkout_approved: string | null;
}

export type RoomStatus = 'ready' | 'occupied' | 'checked_out';

interface RoomBoardRow {
  roomId: string;
  roomName: string;
  roomStatus: RoomStatus;
  outBooking: OpBooking | null;
  stayoverBooking: OpBooking | null;
  inBooking: OpBooking | null;
  task: OpTask | null;
  outGuestOps: OpGuestOp | null;
  inGuestOps: OpGuestOp | null;
  stayoverGuestOps: OpGuestOp | null;
}

interface Props {
  rooms: OpRoom[];
  bookings: OpBooking[];
  tasks: OpTask[];
  guestOps: OpGuestOp[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selectedDate: string;
  onDateChange: (date: string) => void;
  onCheckIn: (bookingId: string) => Promise<void>;
  onCheckOut: (bookingId: string) => Promise<void>;
  onImportedCheckOut: (bookingId: string) => Promise<void>;
  onNoShow: (bookingId: string, reason: string) => Promise<void>;
  actionLoading: boolean;
  actionMessage: string | null;
  actionIsError: boolean;
  onClearMessage: () => void;
  roomStatusBookings: OpBooking[];
  roomStatusTasks: OpTask[];
}

// ── Helpers ──

const ROOM_ORDER = ['Room 101', 'Room 102', 'Room 103', 'Room 201', 'Room 202', 'Room 203'];

function addDaysJST(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  date.setUTCDate(date.getUTCDate() + days);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

function getTodayJST(): string {
  const fmt = new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit' });
  const parts = fmt.formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';
  return `${y}-${m}-${d}`;
}

function formatSelectedDateJST(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const wd = weekdays[date.getUTCDay()];
  return `${y}年${m}月${d}日（${wd}）`;
}

function formatTimeOnly(timeStr: string | null): string {
  if (!timeStr) return '--:--';
  if (/^\d{2}:\d{2}/.test(timeStr)) return timeStr.slice(0, 5);
  return '--:--';
}

function formatJSTTimeFromTimestamptz(isoStr: string | null): string | null {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('ja-JP', { timeZone: 'Asia/Tokyo', hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatJSTDateTimeFromTimestamptz(isoStr: string | null): string | null {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
}

function nightsStayed(arrivalDate: string, selectedDateStr: string): number {
  const [ay, am, ad] = arrivalDate.split('-').map(Number);
  const [sy, sm, sd] = selectedDateStr.split('-').map(Number);
  const a = new Date(Date.UTC(ay, am - 1, ad, 12, 0, 0));
  const s = new Date(Date.UTC(sy, sm - 1, sd, 12, 0, 0));
  const diff = Math.round((s.getTime() - a.getTime()) / (1000 * 60 * 60 * 24));
  return Math.max(1, diff + 1);
}

// ── Status labels ──

const CLEANING_STATUS_LABELS: Record<string, string> = {
  not_started: '未着手',
  assigned: '担当決定',
  in_progress: '清掃中',
  paused: '一時停止',
  completed: '完了',
  waiting_inspection: '点検待ち',
  ready: '準備完了',
  blocked: '要確認',
  not_required: '清掃不要',
};

// ── Colors ──

const C = {
  bg: '#F6F4EE',
  cardBg: '#FFFEFA',
  text: '#252824',
  muted: '#6B6B67',
  primary: '#234638',
  accent: '#A8843A',
  inColor: '#6B5E4A',
  border: '#DED9CC',
  headerBg: '#F0EDE5',
  dash: '#9a9890',
};

// ── Room status computation ──

function computeRoomStatusMap(
  rooms: OpRoom[],
  rsBookings: OpBooking[],
  rsTasks: OpTask[],
  todayJst: string,
): Map<string, RoomStatus> {
  const map = new Map<string, RoomStatus>();

  rooms.forEach((room) => {
    // A. 在室: checked_in booking with no actual_check_out_at, not cancelled
    const occupiedBooking = rsBookings.find(
      (b) =>
        b.room_id === room.id &&
        isCurrentlyInHouse(b),
    );
    if (occupiedBooking) {
      map.set(room.id, 'occupied');
      return;
    }

    // A2. 移行時在室予約（システム導入前からの滞在）
    const importedInHouse = rsBookings.find(
      (b) =>
        b.room_id === room.id &&
        isImportedInHouseBooking(b, todayJst),
    );
    if (importedInHouse) {
      map.set(room.id, 'occupied');
      return;
    }

    // B. 最新のchecked_out予約1件だけを対象にする
    const latestCheckout = rsBookings
      .filter(
        (b) =>
          b.room_id === room.id &&
          b.operational_status === 'checked_out' &&
          b.actual_check_out_at !== null &&
          b.booking_status !== 'cancelled',
      )
      .sort((a, b) => {
        return new Date(b.actual_check_out_at!).getTime() - new Date(a.actual_check_out_at!).getTime();
      })[0];

    // C. チェックアウト履歴なし → 初期状態として準備完了
    if (!latestCheckout) {
      map.set(room.id, 'ready');
      return;
    }

    // D. 最新チェックアウト予約に紐づく清掃タスクを確認
    const linkedTask = rsTasks.find(
      (t) => t.departing_booking_id === latestCheckout.id,
    );

    // E. linkedTask なし → 清掃タスク未作成 → チェックアウト済
    if (!linkedTask) {
      map.set(room.id, 'checked_out');
      return;
    }

    // F. linkedTask が ready / completed / not_required → 準備完了
    if (['completed', 'ready', 'not_required'].includes(linkedTask.status)) {
      map.set(room.id, 'ready');
      return;
    }

    // G. それ以外（not_started / assigned / in_progress / paused / blocked など）→ チェックアウト済
    map.set(room.id, 'checked_out');
  });

  return map;
}

// ── Room status badge ──

function RoomStatusBadge({ status, overdue }: { status: RoomStatus; overdue?: boolean }) {
  if (status === 'occupied') {
    return (
      <span className="inline-flex items-center gap-1">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA' }}>
          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-user-line text-[11px]"></i></span>
          在室
        </span>
        {overdue && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: '#FFF3E0', color: '#A8641A' }}>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-alert-line text-[10px]"></i></span>
            チェックアウト予定超過
          </span>
        )}
      </span>
    );
  }
  if (status === 'checked_out') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap" style={{ backgroundColor: C.accent, color: '#FFFEFA' }}>
        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-logout-box-line text-[11px]"></i></span>
        チェックアウト済
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA', border: `1px solid ${C.primary}` }}>
      <span className="w-3 h-3 flex items-center justify-center"><i className="ri-check-line text-[11px]"></i></span>
      準備完了
    </span>
  );
}

// ── Component ──

export default function RoomOperationBoard({
  rooms, bookings, tasks, guestOps, loading, error, onRetry,
  selectedDate, onDateChange, onCheckIn, onCheckOut, onImportedCheckOut, onNoShow,
  actionLoading, actionMessage, actionIsError, onClearMessage,
  roomStatusBookings, roomStatusTasks,
}: Props) {
  const todayStr = getTodayJST();
  const isToday = selectedDate === todayStr;
  const isPast = selectedDate < todayStr;

  // Detail drawer state
  const [detailBooking, setDetailBooking] = useState<OpBooking | null>(null);
  const [detailRoomName, setDetailRoomName] = useState<string>('');
  const [detailGuestOps, setDetailGuestOps] = useState<OpGuestOp | null>(null);
  const [detailRoomStatus, setDetailRoomStatus] = useState<RoomStatus>('ready');
  const [detailCheckinBlockedReason, setDetailCheckinBlockedReason] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // guestOps map
  const guestOpsMap = useMemo(() => {
    const map = new Map<string, OpGuestOp>();
    guestOps.forEach((op) => map.set(op.booking_id, op));
    return map;
  }, [guestOps]);

  // Room status map
  const roomStatusMap = useMemo(
    () => computeRoomStatusMap(rooms, roomStatusBookings, roomStatusTasks, todayStr),
    [rooms, roomStatusBookings, roomStatusTasks, todayStr],
  );

  // Which rooms have an in-house booking past its departure date
  const roomOverdueMap = useMemo(() => {
    const map = new Map<string, boolean>();
    roomStatusBookings.forEach((b) => {
      const inHouse = isCurrentlyInHouse(b) || isImportedInHouseBooking(b, todayStr);
      if (inHouse && b.departure_date < todayStr) {
        map.set(b.room_id, true);
      }
    });
    return map;
  }, [roomStatusBookings, todayStr]);

  // ── Detail drawer open/close ──
  const openDetail = useCallback((booking: OpBooking, roomName: string, ops: OpGuestOp | null, roomStatus: RoomStatus) => {
    setDetailBooking(booking);
    setDetailRoomName(roomName);
    setDetailGuestOps(ops);
    setDetailRoomStatus(roomStatus);
    // roomStatus を唯一の判定元にする（roomCheckinBlockedMap による重複判定は廃止）
    if (roomStatus === 'occupied') {
      setDetailCheckinBlockedReason('別のゲストが在室中');
    } else if (roomStatus === 'checked_out') {
      setDetailCheckinBlockedReason('清掃完了後にチェックインできます');
    } else {
      setDetailCheckinBlockedReason(null);
    }
    setDrawerOpen(true);
  }, []);

  const closeDetail = useCallback(() => {
    setDrawerOpen(false);
    setTimeout(() => {
      setDetailBooking(null);
      setDetailRoomName('');
      setDetailGuestOps(null);
      setDetailRoomStatus('ready');
      setDetailCheckinBlockedReason(null);
    }, 200);
  }, []);

  // Esc key (must be after closeDetail)
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDetail(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [drawerOpen, closeDetail]);

  // Board rows
  const { boardRows, summary } = useMemo(() => {
    const sorted = [...rooms].sort((a, b) => {
      const ai = ROOM_ORDER.indexOf(a.name);
      const bi = ROOM_ORDER.indexOf(b.name);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

    let outCount = 0;
    let stayoverCount = 0;
    let inCount = 0;
    const occupiedRoomIds = new Set<string>();

    const rows: RoomBoardRow[] = sorted.map((room) => {
      const roomBookings = bookings.filter((b) => b.room_id === room.id);
      const roomTask = tasks.find((t) => t.room_id === room.id) ?? null;

      const outB = roomBookings.find(
        (b) => b.departure_date === selectedDate && b.operational_status !== 'no_show',
      ) ?? null;
      const inB = roomBookings.find(
        (b) => b.arrival_date === selectedDate,
      ) ?? null;
      const stayB = roomBookings.find(
        (b) => b.arrival_date < selectedDate && b.departure_date > selectedDate && b.operational_status !== 'no_show',
      ) ?? null;

      if (outB) outCount++;
      if (inB) {
        inCount++;
        if (inB.operational_status !== 'no_show') {
          occupiedRoomIds.add(room.id);
        }
      }
      if (stayB) { stayoverCount++; occupiedRoomIds.add(room.id); }

      return {
        roomId: room.id,
        roomName: room.name,
        roomStatus: roomStatusMap.get(room.id) ?? 'ready',
        outBooking: outB,
        stayoverBooking: stayB,
        inBooking: inB,
        task: roomTask,
        outGuestOps: outB ? (guestOpsMap.get(outB.id) ?? null) : null,
        inGuestOps: inB ? (guestOpsMap.get(inB.id) ?? null) : null,
        stayoverGuestOps: stayB ? (guestOpsMap.get(stayB.id) ?? null) : null,
      };
    });

    return { boardRows: rows, summary: { outCount, stayoverCount, inCount, occupiedRooms: occupiedRoomIds.size } };
  }, [rooms, bookings, tasks, guestOpsMap, roomStatusMap, selectedDate]);

  // ── Render helpers ──

  const getChannelLabel = (b: OpBooking): string => {
    if (b.source_override) return b.source_override;
    if (b.booking_channel) return b.booking_channel;
    return '—';
  };

  const renderGuestOpBadges = (ops: OpGuestOp | null, type: 'out' | 'in') => {
    if (!ops) return null;
    const badges: React.ReactNode[] = [];
    if (type === 'out') {
      if (ops.late_checkout_approved) {
        const t = formatJSTTimeFromTimestamptz(ops.late_checkout_approved);
        badges.push(<span key="lc-a" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA' }}><span className="w-3 h-3 flex items-center justify-center"><i className="ri-check-double-line text-[10px]"></i></span>Late checkout承認{t ? ` ${t}` : ''}</span>);
      } else if (ops.late_checkout_requested) {
        const t = formatJSTTimeFromTimestamptz(ops.late_checkout_requested);
        badges.push(<span key="lc-r" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: C.accent, color: '#FFFEFA' }}><span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line text-[10px]"></i></span>Late checkout希望{t ? ` ${t}` : ''}</span>);
      }
    }
    if (type === 'in') {
      if (ops.early_checkin_approved) {
        const t = formatJSTTimeFromTimestamptz(ops.early_checkin_approved);
        badges.push(<span key="ec-a" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA' }}><span className="w-3 h-3 flex items-center justify-center"><i className="ri-check-double-line text-[10px]"></i></span>Early check-in承認{t ? ` ${t}` : ''}</span>);
      } else if (ops.early_checkin_requested) {
        const t = formatJSTTimeFromTimestamptz(ops.early_checkin_requested);
        badges.push(<span key="ec-r" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: C.accent, color: '#FFFEFA' }}><span className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line text-[10px]"></i></span>Early check-in希望{t ? ` ${t}` : ''}</span>);
      }
    }
    return badges.length > 0 ? <div className="flex flex-wrap gap-1 mt-1">{badges}</div> : null;
  };

  const renderOutCell = (row: RoomBoardRow) => {
    if (!row.outBooking) return <span className="text-sm" style={{ color: C.dash }}>—</span>;
    const b = row.outBooking;

    // すでにチェックアウト済み → コンパクトな退室済表示
    if (b.actual_check_out_at) {
      const outTime = formatJSTTimeFromTimestamptz(b.actual_check_out_at);
      return (
        <button
          onClick={() => openDetail(b, row.roomName, row.outGuestOps, row.roomStatus)}
          className="text-left w-full cursor-pointer group rounded transition-colors hover:bg-background-100"
          aria-label={`${b.guest_display_name || 'ゲスト'}の詳細を表示（退室済）`}
        >
          <p className="text-xs flex items-center gap-1" style={{ color: C.primary }}>
            <span className="w-3.5 h-3.5 flex items-center justify-center flex-shrink-0"><i className="ri-check-double-line text-xs"></i></span>
            <span className="font-medium whitespace-nowrap">退室済</span>
            {outTime && <span className="whitespace-nowrap" style={{ color: C.muted }}>{outTime}</span>}
          </p>
          <p className="text-[9px]" style={{ color: C.muted }}>{b.guest_display_name || 'ゲスト'} · {getChannelLabel(b)}</p>
          {renderGuestOpBadges(row.outGuestOps, 'out')}
        </button>
      );
    }

    return (
      <button
        onClick={() => openDetail(b, row.roomName, row.outGuestOps, row.roomStatus)}
        className="text-left w-full cursor-pointer group rounded transition-colors hover:bg-background-100"
        aria-label={`${b.guest_display_name || 'ゲスト'}の詳細を表示`}
      >
        <p className="text-xs font-medium truncate group-hover:underline" style={{ color: C.text }}>{b.guest_display_name || 'ゲスト'}</p>
        <p className="text-[9px]" style={{ color: C.muted }}>OUT {formatTimeOnly(b.departure_time)} · {getChannelLabel(b)}</p>
        {renderGuestOpBadges(row.outGuestOps, 'out')}
      </button>
    );
  };

  const renderStayoverCell = (row: RoomBoardRow) => {
    if (!row.stayoverBooking) return <span className="text-sm" style={{ color: C.dash }}>—</span>;
    const b = row.stayoverBooking;
    const n = nightsStayed(b.arrival_date, selectedDate);
    return (
      <button
        onClick={() => openDetail(b, row.roomName, row.stayoverGuestOps, row.roomStatus)}
        className="text-left w-full cursor-pointer group rounded transition-colors hover:bg-background-100"
        aria-label={`${b.guest_display_name || 'ゲスト'}の詳細を表示`}
      >
        <p className="text-xs font-medium truncate group-hover:underline" style={{ color: C.text }}>{b.guest_display_name || 'ゲスト'}</p>
        <p className="text-[9px]" style={{ color: C.muted }}>宿泊 {n}泊目 · {getChannelLabel(b)}</p>
      </button>
    );
  };

  const renderInCell = (row: RoomBoardRow) => {
    if (!row.inBooking) return <span className="text-sm" style={{ color: C.dash }}>—</span>;
    const b = row.inBooking;
    const isBookingNoShow = b.operational_status === 'no_show';
    return (
      <button
        onClick={() => openDetail(b, row.roomName, row.inGuestOps, row.roomStatus)}
        className="text-left w-full cursor-pointer group rounded transition-colors hover:bg-background-100"
        aria-label={`${b.guest_display_name || 'ゲスト'}の詳細を表示`}
      >
        <p className="text-xs font-medium truncate group-hover:underline" style={{ color: C.text }}>{b.guest_display_name || 'ゲスト'}</p>
        {isBookingNoShow ? (
          <p className="text-xs font-medium" style={{ color: '#A33A32' }}>ノーショー</p>
        ) : (
          <p className="text-[9px]" style={{ color: C.muted }}>IN {formatTimeOnly(b.arrival_time)} · {getChannelLabel(b)}</p>
        )}
        {isBookingNoShow && <p className="text-[9px]" style={{ color: C.muted }}>{getChannelLabel(b)}</p>}
        {renderGuestOpBadges(row.inGuestOps, 'in')}
      </button>
    );
  };

  const renderCleaningCell = (row: RoomBoardRow) => {
    const labels: React.ReactNode[] = [];
    if (row.outGuestOps && (row.outGuestOps.late_checkout_requested || row.outGuestOps.late_checkout_approved)) {
      labels.push(<span key="caution" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: C.accent, color: '#FFFEFA' }}><span className="w-3 h-3 flex items-center justify-center"><i className="ri-alert-line text-[10px]"></i></span>開始注意</span>);
    }
    if (row.inGuestOps && (row.inGuestOps.early_checkin_requested || row.inGuestOps.early_checkin_approved)) {
      labels.push(<span key="priority" className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA' }}><span className="w-3 h-3 flex items-center justify-center"><i className="ri-flashlight-line text-[10px]"></i></span>優先清掃</span>);
    }
    if (row.task) {
      const label = CLEANING_STATUS_LABELS[row.task.status] || row.task.status;
      const isActive = ['completed', 'ready', 'in_progress'].includes(row.task.status);
      labels.push(<span key="task" className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap" style={{ backgroundColor: isActive ? C.primary : C.headerBg, color: isActive ? '#FFFEFA' : C.muted }}>{label}</span>);
    } else if (labels.length === 0) {
      return <span className="text-sm" style={{ color: C.dash }}>—</span>;
    }
    return <div className="flex flex-wrap gap-1">{labels}</div>;
  };

  // ── Date badge ──
  const dateBadge = isToday
    ? { label: '今日', bg: C.primary, fg: '#FFFEFA' }
    : isPast
      ? { label: '過去', bg: C.headerBg, fg: C.muted }
      : { label: '予定', bg: '#E8E0C8', fg: C.primary };

  // ── Navigation ──
  const prevDate = () => onDateChange(addDaysJST(selectedDate, -1));
  const nextDate = () => onDateChange(addDaysJST(selectedDate, 1));
  const goToday = () => onDateChange(todayStr);

  // ── Button styles ──
  const navBtnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    height: '36px',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    color: C.text,
    backgroundColor: C.headerBg,
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'background-color 0.15s ease',
  };

  const navBtnHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget.style.backgroundColor = '#E7E2D7');
  };
  const navBtnLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget.style.backgroundColor = C.headerBg);
  };

  const todayBtnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    height: '36px',
    paddingLeft: '12px',
    paddingRight: '12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#FFFEFA',
    backgroundColor: C.primary,
    border: 'none',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    transition: 'opacity 0.15s ease',
  };

  const todayBtnHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget.style.opacity = '0.85');
  };
  const todayBtnLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget.style.opacity = '1');
  };

  const inputStyle = {
    height: '36px',
    paddingLeft: '10px',
    paddingRight: '10px',
    borderRadius: '6px',
    fontSize: '13px',
    color: C.text,
    backgroundColor: C.cardBg,
    border: `1px solid ${C.border}`,
    cursor: 'pointer',
  };

  // ── Loading / Error ──
  if (loading) {
    return (
      <div className="rounded-md border overflow-hidden" style={{ backgroundColor: C.bg, borderColor: C.border }}>
        <div className="flex items-center justify-center py-12">
          <span className="w-5 h-5 flex items-center justify-center"><i className="ri-loader-4-line text-lg animate-spin" style={{ color: C.primary }}></i></span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border overflow-hidden" style={{ backgroundColor: C.bg, borderColor: C.border }}>
        <div className="flex flex-col items-center justify-center py-12 gap-2">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#F0EDE5' }}>
            <i className="ri-error-warning-line text-lg" style={{ color: '#A33A32' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: '#A33A32' }}>データの取得に失敗しました</p>
          <p className="text-xs max-w-md text-center px-4" style={{ color: C.muted }}>{error}</p>
          <button onClick={onRetry} className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap" style={{ backgroundColor: C.primary, color: '#FFFEFA' }}>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-refresh-line text-sm"></i></span>再読込
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {actionMessage && (
        <div className={`mb-3 px-3 py-2 rounded-md text-xs flex items-center gap-2 ${actionIsError ? '' : ''}`} style={actionIsError ? { backgroundColor: '#FDF2F2', color: '#A33A32', border: '1px solid #F5C6C6' } : { backgroundColor: '#F0F7F4', color: C.primary, border: `1px solid ${C.border}` }}>
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className={actionIsError ? 'ri-error-warning-line text-sm' : 'ri-check-line text-sm'}></i></span>
          <span className="flex-1">{actionMessage}</span>
          <button onClick={onClearMessage} className="w-5 h-5 flex items-center justify-center rounded cursor-pointer flex-shrink-0" style={{ color: actionIsError ? '#A33A32' : C.muted }}>
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      <div className="rounded-md border overflow-hidden" style={{ backgroundColor: C.bg, borderColor: C.border }}>
        {/* ── Date Navigation ── */}
        <div className="px-3 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
          {/* Desktop */}
          <div className="hidden md:flex items-center justify-between gap-3">
            {/* Left group */}
            <div className="flex items-center gap-2">
              <button
                onClick={prevDate}
                onMouseEnter={navBtnHover}
                onMouseLeave={navBtnLeave}
                style={navBtnBase}
                aria-label="前日"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line text-sm"></i></span>
                前日
              </button>

              <label htmlFor="operation-date-desktop" className="text-base md:text-xl font-bold whitespace-nowrap px-1 cursor-pointer hover:opacity-70" style={{ color: C.text }} title="カレンダーから日付を選択">
                {formatSelectedDateJST(selectedDate)}
              </label>

              <button
                onClick={nextDate}
                onMouseEnter={navBtnHover}
                onMouseLeave={navBtnLeave}
                style={navBtnBase}
                aria-label="翌日"
              >
                翌日
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-sm"></i></span>
              </button>

              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
                style={{ backgroundColor: dateBadge.bg, color: dateBadge.fg }}
              >
                {dateBadge.label}
              </span>
            </div>

            {/* Right group */}
            <div className="flex items-center gap-2">
              <input
                id="operation-date-desktop"
                type="date"
                value={selectedDate}
                onChange={(e) => onDateChange(e.target.value)}
                style={inputStyle}
                aria-label="日付選択"
              />
              {!isToday && (
                <button
                  onClick={goToday}
                  onMouseEnter={todayBtnHover}
                  onMouseLeave={todayBtnLeave}
                  style={todayBtnBase}
                  aria-label="今日に戻る"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-todo-line text-sm"></i></span>
                  今日に戻る
                </button>
              )}
            </div>
          </div>

          {/* Mobile */}
          <div className="flex md:hidden flex-col gap-2">
            {/* Row 1: prev / date / next / badge */}
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={prevDate}
                onMouseEnter={navBtnHover}
                onMouseLeave={navBtnLeave}
                style={navBtnBase}
                aria-label="前日"
                className="w-[52px]"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line text-sm"></i></span>
                <span className="sr-only">前日</span>
              </button>

              <label htmlFor="operation-date-mobile" className="text-base font-bold whitespace-nowrap px-1 cursor-pointer" style={{ color: C.text }}>
                {formatSelectedDateJST(selectedDate)}
              </label>

              <button
                onClick={nextDate}
                onMouseEnter={navBtnHover}
                onMouseLeave={navBtnLeave}
                style={navBtnBase}
                aria-label="翌日"
                className="w-[52px]"
              >
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-sm"></i></span>
                <span className="sr-only">翌日</span>
              </button>

              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold whitespace-nowrap"
                style={{ backgroundColor: dateBadge.bg, color: dateBadge.fg }}
              >
                {dateBadge.label}
              </span>
            </div>

            {/* Row 2: calendar / today */}
            <div className="flex items-center gap-2">
              <input
                id="operation-date-mobile"
                type="date"
                value={selectedDate}
                onChange={(e) => onDateChange(e.target.value)}
                style={inputStyle}
                className="flex-1 min-w-0"
                aria-label="日付選択"
              />
              {!isToday && (
                <button
                  onClick={goToday}
                  onMouseEnter={todayBtnHover}
                  onMouseLeave={todayBtnLeave}
                  style={todayBtnBase}
                  className="flex-shrink-0"
                  aria-label="今日に戻る"
                >
                  今日に戻る
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Summary bar ── */}
        <div className="px-3 py-2 flex flex-wrap items-center gap-3 text-xs" style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: C.cardBg }}>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.accent }}></span>
            <span style={{ color: C.muted }}>OUT</span>
            <span className="font-semibold" style={{ color: C.text }}>{summary.outCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.primary }}></span>
            <span style={{ color: C.muted }}>STAYOVER</span>
            <span className="font-semibold" style={{ color: C.text }}>{summary.stayoverCount}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: C.inColor }}></span>
            <span style={{ color: C.muted }}>IN</span>
            <span className="font-semibold" style={{ color: C.text }}>{summary.inCount}</span>
          </div>
          <div className="h-3.5 w-px hidden sm:block" style={{ backgroundColor: C.border }}></div>
          <div className="hidden sm:flex items-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-moon-line text-xs" style={{ color: C.primary }}></i></span>
            <span style={{ color: C.muted }}>今夜の稼働</span>
            <span className="font-semibold" style={{ color: C.text }}>{summary.occupiedRooms}室</span>
          </div>
        </div>

        {/* ── Desktop Table ── */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: C.headerBg }}>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide w-[100px]" style={{ color: C.muted }}>Room</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide w-[115px]" style={{ color: C.muted }}>部屋状態</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.muted }}>OUT</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.muted }}>STAYOVER</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.muted }}>IN</th>
                <th className="text-left px-3 py-2 text-[11px] font-semibold uppercase tracking-wide w-[130px]" style={{ color: C.muted }}>清掃</th>
              </tr>
            </thead>
            <tbody>
              {boardRows.map((row) => (
                <tr key={row.roomId} style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: C.cardBg }}>
                  <td className="px-3 py-1">
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: C.primary }}></span>
                      <span className="text-xs font-semibold whitespace-nowrap" style={{ color: C.text }}>{row.roomName.replace(/^Room\s*/i, '')}</span>
                    </div>
                  </td>
                  <td className="px-3 py-1 align-middle">
                    <RoomStatusBadge status={row.roomStatus} overdue={roomOverdueMap.get(row.roomId)} />
                  </td>
                  <td className="px-3 py-1 align-middle">{renderOutCell(row)}</td>
                  <td className="px-3 py-1 align-middle">{renderStayoverCell(row)}</td>
                  <td className="px-3 py-1 align-middle">{renderInCell(row)}</td>
                  <td className="px-3 py-1 align-middle">{renderCleaningCell(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── Mobile Cards ── */}
        <div className="lg:hidden">
          <div className="px-3 py-2 flex items-center justify-between text-xs" style={{ backgroundColor: C.headerBg, borderBottom: `1px solid ${C.border}` }}>
            <span style={{ color: C.muted }}>今夜の稼働</span>
            <span className="font-semibold" style={{ color: C.text }}>{summary.occupiedRooms}室</span>
          </div>
          {boardRows.map((row) => (
            <div key={row.roomId} className="px-3 py-2.5 space-y-2" style={{ borderBottom: `1px solid ${C.border}`, backgroundColor: C.cardBg }}>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: C.primary }}></span>
                <span className="text-sm font-semibold" style={{ color: C.text }}>{row.roomName}</span>
                <RoomStatusBadge status={row.roomStatus} overdue={roomOverdueMap.get(row.roomId)} />
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-semibold uppercase w-16 flex-shrink-0 pt-0.5" style={{ color: C.accent }}>OUT</span>
                <div className="flex-1 min-w-0">{renderOutCell(row)}</div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-semibold uppercase w-16 flex-shrink-0 pt-0.5" style={{ color: C.primary }}>STAY</span>
                <div className="flex-1 min-w-0">{renderStayoverCell(row)}</div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-semibold uppercase w-16 flex-shrink-0 pt-0.5" style={{ color: C.inColor }}>IN</span>
                <div className="flex-1 min-w-0">{renderInCell(row)}</div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[10px] font-semibold uppercase w-16 flex-shrink-0 pt-0.5" style={{ color: C.muted }}>清掃</span>
                <div className="flex-1 min-w-0">{renderCleaningCell(row)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Booking Detail Drawer ── */}
      <BookingDetailDrawer
        open={drawerOpen}
        booking={detailBooking}
        roomName={detailRoomName}
        guestOps={detailGuestOps}
        roomStatus={detailRoomStatus}
        checkinBlockedReason={detailCheckinBlockedReason}
        todayJst={todayStr}
        onClose={closeDetail}
        onCheckIn={onCheckIn}
        onCheckOut={onCheckOut}
        onImportedCheckOut={onImportedCheckOut}
        onNoShow={onNoShow}
        actionLoading={actionLoading}
      />
    </>
  );
}

export { formatJSTTimeFromTimestamptz, formatJSTDateTimeFromTimestamptz };
